let orchInstance = null;
const initApiResponseCache = {
  checkout: {},
  completeIdentityValidation: {},
  getSrcProfile: {},
  identityLookUp: {},
  initiateIdentityValidation: {},
  isRecognized: {},
  unbindAppInstance: {},
  uniqueIdTokens: []
};

/**
 * OrchCustomData Class used for mapping Orchestartion data
 *  - Singleton class
 */
class OrchCustomData {
  /**
   * Default constructor
   *   - Multiple instantiation will provide same instance(First time instance)
   */
  constructor() {
    if (!orchInstance) {
      orchInstance = this; // eslint-disable-line consistent-this
    }
    this.apikey = null;
    this.apiTimeStamp = { getSrcProfile: {}, init: {}, isRecognized: {}, unbindAppInstance: {} };
    this.currVInitConfigs = {};
    this.isCheckoutInvoked = false;
    this.isGetSRCProfileInvoked = false;
    this.initData = {};
    this.intentRequestPayload = {};
    this.srcSdks = {
      apiResponseCache: JSON.parse(JSON.stringify(initApiResponseCache)),
      appConfig: {
        apikey: '',
        merchantConfig: {},
        supportedNetworks: [],
        vInitRequest: {}
      },
      isRecognizedPromiseArr: [],
      loadedSdk: {},
      orchConfig: {
        apiTimeout: 500,
        initData: {
          dpaData: {},
          dpaTransactionOptions: {}
        },
        otpDeliveryChannel: '',
        preferredNetwork: '',
        showVdcpIno: true, //default to true for INO use case
        windowReference: null
      }
    };
    this.srcInitiatorId = { AMEX: null, DISCOVER: null, MA: null, VISA: null };
    this.isVsb = false; // true if initializeVsb was called at least once
    this.isVsbButtonless = false; // true for vsb-integrated flow, false for vsb-button and all other flows
    this.initSuccessfulNetworks = []; // list of networks that successfully initialized
    this.isTokenizationEnabled = false;
    return orchInstance;
  }

  clearApiResponseCache() {
    this.srcSdks.apiResponseCache = JSON.parse(JSON.stringify(initApiResponseCache));
  }
  /**
   * Returns orchCustomProps
   * @returns {object} _orchCustomProps
   */
  get orchCustomProps() {
    return this.srcSdks;
  }
  /**
   * delete network on load failure/merchant does not support
   * @param {string} network
   */
  removeNetwork(network) {
    const supportedNetworks = this.srcSdks.appConfig.supportedNetworks;
    const index = supportedNetworks.indexOf(network);
    //apply splice iff network is present
    if (index >= 0) {
      supportedNetworks.splice(index, 1);
    }
  }
  /**
   * Sets checkout response
   * @param {object} response
   */
  setCheckoutResponse(response) {
    Object.assign(this.srcSdks.apiResponseCache.checkout, response);
  }
  /**
   * Sets dpaLocale from config object
   * @param {string} dpaLocale
   */
  setDefaultDpaLocale(dpaLocale) {
    if (dpaLocale) {
      this.srcSdks.orchConfig.dpaLocale = dpaLocale;
    }
  }
  /**
   * Sets identityLookUp
   * @param {object} identityResponseData
   */
  setIdentityLookup(identityResponseData) {
    Object.assign(this.srcSdks.apiResponseCache.identityLookUp, identityResponseData);
  }
  /**
   * Sets identityLookUp
   * @param {object} initiateIdentityResponse
   */
  setInitiateIdentity(initiateIdentityResponse) {
    Object.assign(
      this.srcSdks.apiResponseCache.initiateIdentityValidation,
      initiateIdentityResponse
    );
  }
  /**
   * Sets the loaded network sdk instance to the custom object
   * @param {object} networkInstance
   */
  setLoadedSdk(networkInstance) {
    Object.assign(this.srcSdks.loadedSdk, networkInstance);
  }
  /**
   * Sets network for all participating sdks
   * @param {string} network
   */
  setNetwork(network) {
    const supportedNetworks = this.srcSdks.appConfig.supportedNetworks;
    if (supportedNetworks.includes(network) === false) {
      supportedNetworks.push(network);
    }
  }
  /**
   * Sets Otp Delivery channel
   * @param {string} otpChannel
   */
  setOTPDeliveryChannel(otpChannel) {
    if (otpChannel) {
      this.srcSdks.orchConfig.otpDeliveryChannel = otpChannel;
    }
  }
  /**
   * Sets participants and prevents recurring changes to inital value
   * @param {object} participants
   */
  setParticipants(participants) {
    Object.assign(this.srcSdks.loadedSdk, participants);
  }
  /**
   * Sets participants and prevents recurring changes to inital value
   * @param {object} isRecognizedResponse
   */
  setRecognized(isRecognizedResponse) {
    Object.assign(this.srcSdks.apiResponseCache.isRecognized, isRecognizedResponse);
  }
  /**
   * Set showVdcpIno
   * @param {string} showVdcpIno
   */
  setShowVdcpIno(showVdcpIno) {
    this.srcSdks.orchConfig.showVdcpIno = showVdcpIno;
  }
  /**
   * Sets cardlist
   * @param {object} profile
   */
  setSrcProfile(profile) {
    Object.assign(this.srcSdks.apiResponseCache.getSrcProfile, profile);
  }
  /**
   * Set src reference
   * @param {object} instance
   */
  setSrcReference(instance) {
    Object.assign(this.srcSdks.orchConfig, instance);
  }
  /**
   * Set unbind api response
   * @param {object} unbindResponseData
   */
  setUnbind(unbindResponseData) {
    Object.assign(this.srcSdks.apiResponseCache.unbindAppInstance, unbindResponseData);
  }
  /**
   * updateIdToken for all participating sdks
   * @param {object} idToken
   */
  updateIdToken(idToken) {
    this.srcSdks.apiResponseCache.uniqueIdTokens = [idToken];
  }
  /**
   * set based on whether V.initializeVsb has been called
   * @param {boolean} isVsb
   */
  setVsbFlag(isVsb) {
    this.isVsb = isVsb;
  }
  /**
   * set based on most recent init call
   * V.initializeVsb: isVsbButtonless=true
   * V.init: isVsbButtonless=false
   * @param {boolean} isVsbButtonless
   */
  setVsbButtonlessFlag(isVsbButtonless) {
    this.isVsbButtonless = isVsbButtonless;
  }

  /**
   * Need to cache list of successfully initialized
   * networks to send in subsequent multi-vinit
   * responses
   * @param {Array} networks
   */
  setInitSuccessfulNetworks(networks) {
    this.initSuccessfulNetworks = networks;
  }

  /**
   * isTokenizationEnabled flag
   * @param {Boolean} isTokenizationEnabled
   */
  setTokenizationEnabled(isTokenizationEnabled) {
    this.isTokenizationEnabled = isTokenizationEnabled;
  }
}

export default new OrchCustomData();
